/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class MetadataColumnAPI
 * @hideconstructor
 * @classdesc implementation of the MetadataColumnAPI interface.
 */
define([], () => {
	class MetadataColumnAPI {

		/**
		 * @function MetadataColumnAPI#getObjectType
		 * @description Return the object type (e.g. 'QueryItem').
		 * @public
		 *
		 * @return {String} objectType
		 */
		getObjectType() {}

		/**
		 * @function MetadataColumnAPI#getId
		 * @description Return the metadata column unique indentifier.
		 * @public
		 *
		 * @return {String} unique identifier
		 */
		getId() {}

		/**
		 * @function MetadataColumnAPI#getSourceId
		 * @description Return the unique identifier of the metadata source.
		 * @public
		 *
		 * @return {String} unique data source identifier
		 */
		getSourceId() {}

		/**
		 * @function MetadataColumnAPI#getLabel
		 * @description Return the display label of the metadata column.
		 * @public
		 *
		 * @return {String} display label
		 */
		getLabel() {}

		/**
		 * @function MetadataColumnAPI#getType
		 * @description Return the column type (e.g. 'attribute', 'fact').
		 * @public
		 *
		 * @return {String} type
		 */
		getType() {}

		/**
		 * @function MetadataColumnAPI#getDataType
		 * @description Return the column data type (e.g. 'integer', 'string', etc ).
		 * @public
		 *
		 * @return {String} type  - possible values: 'integer' 'decimal', 'double', 'float', 'string', 'date', 'datetime', 'time'
		 *
		 *
		 */
		getDataType() {}

		/**
		 * @function MetadataColumnAPI#getDefaultAggregation
		 * @description Return the column default aggregation.
		 * @public
		 *
		 * @return {String} defaultAggregation
		 */
		getDefaultAggregation() {}

		/**
		 * @function MetadataColumnAPI#getAggregationMode
		 * @description Return the column aggregation mode.
		 * @public
		 *
		 * @return {String} aggregationMode
		 */
		getAggregationMode() {}


		/**
		 * @function MetadataColumnAPI#getTaxonomyList
		 * @description Return the list of column taxonomy.
		 * @public
		 *
		 * @return {TaxonomyAPI[]} taxnonomy list
		 */
		getTaxonomyList() {}


		/**
		 * @function MetadataColumnAPI#getDefaultSortType
		 * @description Return the column sort type.
		 * @public
		 *
		 * @return {String} sortType
		 */
		getDefaultSortType() {}

		/**
		 * @function MetadataColumnAPI#getFormat
		 * @description Return the column data format setting.
		 * @public
		 *
		 * @param {Object} options ignoreDefaultFormatting - Ignore any default formatting
		 * @return {FormatSpec} formatSpec
		 */
		getFormat(/* options */) {}

		/**
		 * @public
		 * @function MetadataColumnAPI#getRootMember
		 * @description Return the rootmember id when the column is a hierarchy.
		 * @return {String} rootMember
		 */
		getRootMember() {}

		/**
		 * @async
		 * @public
		 * @function MetadataColumnAPI#isLeafMember
		 * @description Determines whether a member is a leaf member.
		 * @param {String} memberId Member unique name of the given member
		 * @return {Promise<Boolean>} True if the member is a leaf, otherwise false
		 */
		isLeafMember() {}

		/**
		 * @function MetadataColumnAPI#setLabel
		 * @description Set the display label of the metadata column.
		 * @param {String} label
		 * @public
		 */
		setLabel(/* label */) {}

		/**
		 * @function MetadataColumnAPI#setHidden
		 * @description Hide or show the column
		 * @param {Boolean} bool
		 * @public
		 */
		setHidden(/* bool */) {}

		/**
		 * @function MetadataColumnAPI#isFacetDefinitionEnabled
		 * @description Determine whether the facet definition is enabled.
		 * @public
		 *
		 * @return {Boolean} true if facet definition is enabled, otherwise false
		 *
		 */
		isFacetDefinitionEnabled() {}

		/**
		 * @function MetadataColumnAPI#getSourceCategory
		 * @description Returns the source category of the  column, such as 'hierarchy', or 'level', etc. For relational type of dataset, this should return as 'column'.
		 * @public
		 *
		 * @return {String} sourceCategory
		 **/
		getSourceCategory() {}

		/**
		 * @function MetadataColumnAPI#isHierarchy
		 * @description Determine whether the column is an OLAP hierarchy.
		 * @public
		 *
		 * @return {Boolean} true if the column is an OLAP hierarchy, otherwise false
		 *
		 **/
		isHierarchy() {}

		/**
		 * @function MetadataColumnAPI#isNamedSet
		 * @description Determine whether the column is a named set.
		 * @public
		 *
		 * @return {Boolean} true if hte column is a named set, otherwise false
		 *
		 **/
		isNamedSet() {}

		/**
		 * @function MetadataColumnAPI#isProperty
		 * @description Determine whether the column is a property.
		 * @public
		 *
		 * @return {Boolean} true if the column is a property, otherwise false
		 *
		 **/
		isProperty() {}

		/**
		 * @function MetadataColumnAPI#isLevel
		 * @description Determine whether the column is a level.
		 * @public
		 *
		 * @return {Boolean} true if the column is a level, otherwise false
		 *
		 **/
		isLevel() {}

		/**
		 * @function MetadataColumnAPI#isSingleRootHierarchy
		 * @description Determine whether the column is a single root hierarchy
		 * @public
		 *
		 * @return {Boolean} true if the column is a single root hierarchy, otherwise false
		 *
		 **/
		isSingleRootHierarchy() {}

		/**
		 * @function MetadataColumnAPI#getReferencedHierarchyId
		 * @description Returns the referenced hierarchy when it exists. For example, the referenced hierarchy of a namedSet object
		 * is the hierarchy the namedSet belongs to
		 * @public
		 *
		 * @return {string} hierarchy identifier
		 */
		getReferencedHierarchyId() {}

		/**
		 * @function MetadataColumnAPI#isOlapColumn
		 * @description Determine whether the column is an OLAP column
		 * @public
		 *
		 * @return {Boolean} true if the column is an OLAP column, otherwise false
		 */
		isOlapColumn() {}

		/**
		 * @function MetadataColumnAPI#isNumericDataType
		 * @description Determine whether the column is a number data type (e.g. 'integer', 'decimal', etc) column
		 * @public
		 *
		 * @return {Boolean} true if the column is a number type, otherwise false
		 */
		isNumericDataType() {}

		/**
		 * @function MetadataColumnAPI#isDateTimeDataType
		 * @description Determine whether the column is a data/time data type (e.g. 'date', 'time', etc) column
		 * @public
		 * @return {Boolean} true if the column is a date/time type, otherwise false
		 */
		isDateTimeDataType() {}

		/**
		 * @function MetadataColumnAPI#isFilter
		 * @description Determine whether the column is a filter
		 * @public
		 * @return {Boolean} true if the column is a filter, otherwise false
		 */
		isFilter() {}

		/**
		 * @function MetadataColumnAPI#isEditableCalculation
		 * @description Determine whether the column is an editable calculation
		 * @public
		 *
		 * @return {Boolean} true if the column is an editable calculation, otherwise false
		 */
		isEditableCalculation() {}

		/**
		 * @function MetadataColumnAPI#getTableId
		 * @description Get table id
		 * @public
		 *
		 * @return {Boolean} true if the column is an editable calculation, otherwise false
		 */
		getTableId() {}

		/**
		 * @function MetadataColumnAPI#getTableName
		 * @description Get the table name that this column belongs to
		 * @public
		 *
		 * @return {String} tableName
		 */
		getTableName() {}

		/**
		 * @public
		 * @deprecated
		 * @function MetadataColumnAPI#getDeprecatedMetadataColumn
		 * @description Get the legacy metadata column object
		 * @return {Object} legacy metadata column object
		 *
		 *  @TODO - livewidget_cleanup - demolish this API
		 */
		getDeprecatedMetadataColumn() {}

		/**
		 * @function MetadataColumnAPI#isHidden
		 * @description Determine whether the column is hidden
		 * @public
		 *
		 * @return {Boolean} true if the column is hidden, otherwise false
		 */
		isHidden() {}

		/**
		 * @function MetadataColumnAPI#isMissing
		 * @description Determine whether the column is missing
		 * @public
		 *
		 * @return {Boolean} true if the column is missing, otherwise false
		 */
		isMissing() {}

		/**
		 * @function MetadataColumnAPI#getParent
		 * @description Returns the parent MetadataColumnAPI
		 * @public
		 *
		 * @return {MetadataColumnAPI} MetadataColumnAPI or null
		 */
		getParent() {}

		/**
		 * @function MetadataColumnAPI#getHierarchyLevelIds
		 * @description Returns the levelIds of the hierarchy in natural order.
		 * @public
		 *
		 * @return {String[]} an array of levelIds
		 */
		getHierarchyLevelIds() {}
	}

	return MetadataColumnAPI;
});
